/**
 * Add element to array end.
 *
 * @param arr
 * @param newEntry
 * @returns {*[]}
 */
export function arrPush(arr, newEntry) {
  return [...arr, newEntry];
}

/**
 * Add elements to array end.
 *
 * @param arr
 * @param newEntries
 * @returns {*[]}
 */
export function arrPushMany(arr, newEntries) {
  return [...arr, ...newEntries];
}

/**
 * Add element in begin of array.
 *
 * @param arr
 * @param newEntry
 * @returns {*[]}
 */
export function arrPushFirst(arr, newEntry) {
  return [newEntry, ...arr];
}

/**
 * Remove last element from array.
 *
 * @param arr
 * @returns { *[] }
 */
export function arrPop(arr) {
  return arr.slice(0, -1);
}

/**
 * Remove first element from array.
 *
 * @param arr
 * @returns {*[]}
 */
export function arrShift(arr) {
  return arr.slice(1);
}

/**
 * Remove n elements from specific index, and insert new items at this position.
 *
 * @param arr
 * @param start
 * @param deleteCount
 * @param {*[]} items
 * @returns {*[]}
 */
function arrSplice(arr, start, deleteCount, items) {
  return [...arr.slice(0, start), ...items, ...arr.slice(start + deleteCount)];
}

/**
 * Insert new item at specific position.
 *
 * @param arr
 * @param index
 * @param item
 * @returns {*[]}
 */
export function arrInsert(arr, index, item) {
  return arrSplice(arr, index, 0, [item]);
}

/**
 * Insert new items at specific position.
 *
 * @param arr
 * @param index
 * @param items
 * @returns {*[]}
 */
export function arrInsertMany(arr, index, items) {
  return arrSplice(arr, index, 0, items);
}

/**
 * Remove array element at position.
 *
 * @param arr
 * @param index
 * @returns {*[]}
 */
export function arrDelete(arr, index) {
  return arrSplice(arr, index, 1, []);
}

/**
 * Remove array elements at position.
 *
 * @param arr
 * @param index
 * @param deleteCount
 * @returns {*[]}
 */
export function arrDeleteMany(arr, index, deleteCount) {
  return arrSplice(arr, index, deleteCount, []);
}

/**
 * Update object-element properties in array at position.
 *
 * @param arr
 * @param index
 * @param value
 * @returns {*[]}
 */
export function arrUpdate(arr, index, value) {
  return arrSplice(arr, index, 1, [
    isObject(value) ? objUpdate(arr[index], value) : value
  ]);
}

/**
 * Replace array element with new one.
 *
 * @param arr
 * @param index
 * @param item
 * @returns {*[]}
 */
export function arrReplace(arr, index, item) {
  return arrSplice(arr, index, 1, [item]);
}

/**
 * Toggle value in array.
 *
 * @param arr
 * @param value
 * @returns {*[]}
 */
export function arrToggle(arr, value) {
  const hasValAtIndex = arr.findIndex(item => item === value);
  if (hasValAtIndex !== -1) {
    return arrDelete(arr, hasValAtIndex);
  } else {
    return arrPush(arr, value);
  }
}

export const arrReorder = (arr, fromIndex, toIndex) => {
  if (toIndex > fromIndex) {
    return [
      ...arr.slice(0, fromIndex),
      ...arr.slice(fromIndex + 1, toIndex + 1),
      arr[fromIndex],
      ...arr.slice(toIndex + 1)
    ];
  } else {
    return [
      ...arr.slice(0, toIndex),
      arr[fromIndex],
      ...arr.slice(toIndex, fromIndex),
      ...arr.slice(fromIndex + 1)
    ];
  }
};

/**
 * Update object with new properties.
 *
 * @param obj
 * @param newProps
 * @returns {*}
 */
export function objUpdate(obj, newProps) {
  return Object.assign({}, obj, newProps);
}

/**
 * Make a copy of object with primitives properties.
 *
 * @param obj
 * @returns {*}
 */
export function objLightCopy(obj) {
  return obj && Object.assign({}, obj);
}

/**
 * Make a full copy of object.
 *
 * @param obj
 * @returns {any}
 */
export function objFullCopy(obj) {
  return obj && JSON.parse(JSON.stringify(obj));
}

/**
 * Make a LightCopy of elements to new array.
 *
 * @param arr
 * @returns {*[]}
 */
export function arrLightCopy(arr) {
  return arr && arr.map(item => (isObject(item) ? objLightCopy(item) : item));
}

/**
 * Check if variable is an Object.
 *
 * @param test
 * @returns {boolean}
 */
export function isObject(test) {
  return test !== undefined && test !== null && test.constructor === Object;
}

/**
 * Set element to map.
 *
 * @param map
 * @param key
 * @param entry
 * @returns {Map}
 */
export function mapSet(map, key, entry) {
  const newMap = new Map(map);
  newMap.set(key, entry);

  return newMap;
}

/**
 * Set element to map.
 *
 * @param map
 * @param key
 * @param props
 * @returns {Map}
 */
export function mapUpdateProperty(map, key, props) {
  const newMap = new Map(map);
  const entry = map.get(key);
  newMap.set(key, {
    ...entry,
    ...props
  });
  return newMap;
}

/**
 * Remove element from map.
 *
 * @param map
 * @param key
 * @returns {Map}
 */
export function mapRemove(map, key) {
  const newMap = new Map(map);
  newMap.delete(key);

  return newMap;
}

/**
 * Filter map entries.
 *
 * @param {Map} map
 * @param {(key, entry) => boolean} filterFunction
 * @returns {Map}
 */
export function mapFilter(map, filterFunction) {
  const newMap = new Map();
  for (const [key, entry] of map) {
    if (filterFunction(key, entry)) {
      newMap.set(key, entry);
    }
  }

  return newMap;
}
